// src/app/context/ImpersonationContext.tsx

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { getSessionToken as getDescopeSessionToken } from '@descope/react-sdk';

/**
 * Whose organization and center_user the app is acting as right now.
 *
 * With an impersonation session active this is that session (`sessionId` set,
 * `isImpersonating` true). Otherwise it is the signed-in session's OWN scope, supplied
 * to the provider as `sessionScope` — the active organization and the acting
 * center_user resolved at start-up — with `sessionId`/`reason` null; or null until that
 * scope is known. Every org/user-scoped hook reads its scope from here. Before Descope
 * the PWA minted a "self-impersonation" token at start-up that filled it for the user's
 * own session; that mint is gone (specs/descope-poc.md), so the session fills it now.
 */
export interface ActingDetails {
  targetUserId: string;
  organizationId: string;
  sessionId: string | null;
  reason: string | null;
  expiresAt?: number;
}

/** The signed-in session's own scope; either field may be unknown while initialization runs. */
export interface SessionScope {
  organizationId?: string | null;
  userId?: string | null;
}

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonationToken: string | null;
  impersonationDetails: ActingDetails | null;
  startImpersonation: (
    targetUserId: string,
    organizationId: string,
    reason: string
  ) => Promise<void>;
  endImpersonation: () => Promise<void>;
  getEffectiveToken: () => Promise<string | undefined>;
}

const DEBUG = false; // flip to true for debugging
const log = (...a: any[]) => { if (DEBUG) console.log('[impctx]', ...a); };
const warn = (...a: any[]) => { if (DEBUG) console.warn('[impctx]', ...a); };
const error = (...a: any[]) => console.error('[impctx]', ...a);

/**
 * Renew this many ms BEFORE `expiresAt`. The impersonation token's default life is one hour
 * (`impersonateUser.ts` — `expiresIn` defaults to 3600), so a session outliving it was routine:
 * `getEffectiveToken` then fell through to the plain B2C token, which carries no
 * `organization_id` claim, and every org-scoped read began failing while the UI still showed the
 * user as impersonating. Renewing slightly early keeps an in-flight request from racing the
 * expiry it just crossed.
 */
const IMPERSONATION_REFRESH_WINDOW_MS = 5 * 60 * 1000;

interface MintedImpersonation {
  access_token: string;
  session_id: string;
  expires_in: number;
}

/** The single `/auth/impersonate/v2` call, shared by start and renew so they cannot drift. */
async function mintImpersonationToken(
  b2cToken: string,
  targetUserId: string,
  organizationId: string,
  reason: string,
): Promise<MintedImpersonation> {
  const url = `${process.env.REACT_APP_ORGANIZATION_ENDPOINT}/auth/impersonate/v2`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${b2cToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ targetUserId, organizationId, reason, expiresIn: 3600 }),
  });
  if (!response.ok) {
    const body = await response.text().catch(() => '<no-body>');
    error('impersonate/v2 failed', { status: response.status, body });
    throw new Error(`Failed to start impersonation (${response.status})`);
  }
  return response.json();
}

/**
 * Refreshes an EXISTING impersonation session rather than starting a new one.
 *
 * Renewal used to re-mint through `/auth/impersonate/v2`, which re-runs the full
 * SpiceDB authorization, can 429 on the active-session cap, and — because it reuses
 * the session's `jti` without moving its `expires_at` — handed back a token that
 * outlived the session backing it. The client then trusted its own fresh-looking
 * expiry, never re-impersonated, and every request silently degraded.
 *
 * `/auth/impersonate/refresh` is the endpoint for this: it verifies the session is
 * yours and active, slides a self-impersonation session forward, and bounds the token
 * to the session's remaining life so the two can never diverge again.
 *
 * Returns undefined when the session is genuinely gone (403/404), which the caller
 * treats as "fall back to a full re-impersonation" rather than as a hard failure.
 */
async function refreshImpersonationSession(
  b2cToken: string,
  sessionId: string,
): Promise<MintedImpersonation | undefined> {
  const url = `${process.env.REACT_APP_ORGANIZATION_ENDPOINT}/auth/impersonate/refresh`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${b2cToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sessionId, expiresIn: 3600 }),
  });
  if (response.status === 403 || response.status === 404) {
    warn('impersonate/refresh: session no longer refreshable; will re-impersonate', {
      status: response.status,
    });
    return undefined;
  }
  if (!response.ok) {
    const body = await response.text().catch(() => '<no-body>');
    error('impersonate/refresh failed', { status: response.status, body });
    throw new Error(`Failed to refresh impersonation (${response.status})`);
  }
  return response.json();
}

export const ImpersonationContext = createContext<ImpersonationContextType | null>(null);

interface ImpersonationDetails {
  targetUserId: string;
  organizationId: string;
  sessionId: string;
  reason: string;
  expiresAt?: number;
}

export interface ImpersonationProviderProps {
  children: React.ReactNode;
  /**
   * Supplies the identity token used to mint/renew/end an impersonation session,
   * and as the final fallback in `getEffectiveToken` when no impersonation is
   * active. Defaults to Descope's session token (Center-PWA, EAINT-954 Phase 4);
   * a host that authenticates some other way passes its own.
   */
  getFallbackToken?: () => Promise<string | undefined>;
  /**
   * The signed-in session's own acting scope (see `ActingDetails`). Surfaced as
   * `impersonationDetails` whenever no impersonation session is active. Center-PWA
   * passes the active organization and the resolved `userProfile.userId`; a host with no
   * session scope leaves it unset and gets `null` outside impersonation.
   */
  sessionScope?: SessionScope | null;
}

const defaultGetFallbackToken = async (): Promise<string | undefined> => getDescopeSessionToken() ?? undefined;

export function ImpersonationProvider({
  children,
  getFallbackToken = defaultGetFallbackToken,
  sessionScope = null,
}: Readonly<ImpersonationProviderProps>) {
  const [impersonationToken, setImpersonationToken] = useState<string | null>(null);
  const [impersonationDetails, setImpersonationDetails] = useState<ImpersonationDetails | null>(null);

  // The token is mirrored into a ref so `getEffectiveToken` can read the current
  // value without listing `impersonationToken` as a dependency. Its identity must
  // stay stable: ApiClientsProvider memoizes all six axios clients on it, so a new
  // function identity recreates every client, hands the ApiClients context a new
  // object, and re-fires every `apiClients`-dependent effect in the app. The token
  // is still read fresh on every request through the clients' `tokenProvider`.
  const impersonationTokenRef = useRef<string | null>(null);

  const applyImpersonationToken = useCallback((token: string | null) => {
    impersonationTokenRef.current = token;
    setImpersonationToken(token);
  }, []);

  /**
   * Details setter that is a NO-OP when nothing actually changed.
   *
   * `getEffectiveToken` is the axios `tokenProvider` — it runs on EVERY request, and its
   * storage-hydration branches re-`JSON.parse` the stored details. Passing that freshly-parsed
   * object to `setImpersonationDetails` handed React a new identity every time, so a plain
   * token read re-rendered this provider and, through it, every consumer of the Impersonation
   * and ApiClients contexts. Any component mounting a hook whose effect depends on
   * `graphClient` (e.g. `useConversation`) then re-ran that effect, and its own
   * `setPerms`/`setMessages`/`setPage` calls allocate fresh identities too — a render storm
   * that stayed invisible in the network tab because the conversation request cache absorbed
   * the duplicate fetches.
   *
   * Same class as the `impersonationTokenRef` note above: in this provider an
   * unstable identity is not a perf nit — it re-fires `apiClients`-dependent effects app-wide.
   * The ref mirrors the state so the comparison never becomes a dependency.
   */
  const impersonationDetailsRef = useRef<ImpersonationDetails | null>(null);

  const applyImpersonationDetails = useCallback((details: ImpersonationDetails | null) => {
    const current = impersonationDetailsRef.current;
    const unchanged =
      current === details ||
      (!!current &&
        !!details &&
        current.targetUserId === details.targetUserId &&
        current.organizationId === details.organizationId &&
        current.sessionId === details.sessionId &&
        current.reason === details.reason &&
        current.expiresAt === details.expiresAt);
    if (unchanged) return;
    impersonationDetailsRef.current = details;
    setImpersonationDetails(details);
  }, []);

  // `getFallbackToken` can be a fresh function identity on every render of the host
  // app (e.g. center-auth's inline closure over `pca.getAllAccounts()`). Reading it
  // through a ref keeps `getEffectiveToken` stable: it is a dependency of
  // ApiClientsProvider's memo, so a new identity there rebuilds all six clients and
  // re-fires every `apiClients`-dependent effect in the app — mid user-switch, that
  // re-fetched the entire metaspace for the sphere being navigated away from.
  const getFallbackTokenRef = useRef(getFallbackToken);
  getFallbackTokenRef.current = getFallbackToken;

  // De-dupes concurrent renewals. Every in-flight request calls `getEffectiveToken`, so an
  // expiry reached under load would otherwise fire one `/auth/impersonate/v2` per request and
  // each would invalidate the previous session.
  const renewalInFlightRef = useRef<Promise<string | undefined> | null>(null);

  const startImpersonation = useCallback(async (
    targetUserId: string,
    organizationId: string,
    reason: string
  ) => {
    log('startImpersonation called', { targetUserId, organizationId, reason });

    try {
      const b2cToken = await getFallbackTokenRef.current();
      log('Obtained B2C token for /impersonate/v2 call');

      if (!b2cToken) {
        throw new Error('No authentication token available — please sign in');
      }

      const data = await mintImpersonationToken(b2cToken, targetUserId, organizationId, reason);
      log('Impersonation started', { sessionId: data.session_id, expiresIn: data.expires_in });

      const expiresAt = Date.now() + (data.expires_in * 1000);

      applyImpersonationToken(data.access_token);
      applyImpersonationDetails({
        targetUserId,
        organizationId,
        sessionId: data.session_id,
        reason,
        expiresAt
      });

      sessionStorage.setItem('impersonation_token', data.access_token);
      sessionStorage.setItem('impersonation_details', JSON.stringify({
        targetUserId,
        organizationId,
        sessionId: data.session_id,
        reason,
        expiresAt
      }));

      log('Impersonation state persisted to sessionStorage', { expiresAt });

    } catch (e) {
      error('startImpersonation error', e);
      throw e;
    }
  }, [applyImpersonationToken, applyImpersonationDetails]);

  const endImpersonation = useCallback(async () => {
    if (!impersonationDetails?.sessionId) {
      warn('endImpersonation called but no sessionId present');
      // still clear local state just in case
      applyImpersonationToken(null);
      applyImpersonationDetails(null);
      sessionStorage.removeItem('impersonation_token');
      sessionStorage.removeItem('impersonation_details');
      return;
    }

    log('endImpersonation called', { sessionId: impersonationDetails.sessionId });

    try {
      const b2cToken = await getFallbackTokenRef.current();
      const url = `${process.env.REACT_APP_ORGANIZATION_ENDPOINT}/auth/impersonate/end`;
      log('POST', url);

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${b2cToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ sessionId: impersonationDetails.sessionId })
      });

      if (res.ok) {
        log('End impersonation succeeded');
      } else {
        const body = await res.text().catch(() => '<no-body>');
        warn('End impersonation returned non-OK', { status: res.status, body });
      }
    } catch (e) {
      error('endImpersonation network error (will still clear local state)', e);
    } finally {
      // Clear impersonation state locally no matter what
      applyImpersonationToken(null);
      applyImpersonationDetails(null);
      sessionStorage.removeItem('impersonation_token');
      sessionStorage.removeItem('impersonation_details');
      log('Local impersonation state cleared');
    }
  }, [impersonationDetails, applyImpersonationToken, applyImpersonationDetails]);

  // Get the effective token (impersonation or regular B2C)
  /**
   * Mints a fresh impersonation token for the session already recorded in sessionStorage.
   *
   * Exists because expiry used to degrade SILENTLY: `getEffectiveToken` fell through to the
   * plain B2C token, which has no `organization_id`, so org-scoped reads failed with errors
   * that blamed the caller (`schemas?systemNames=…` returning "organizationId is required")
   * while the UI still presented an impersonated session. Worse than the error was the
   * ambiguity: a request that DID succeed ran as the real user, not the impersonated target.
   *
   * Renewal failure deliberately does NOT fall back to B2C. Running as the wrong principal is
   * not a degraded version of impersonation — it is a different session, and silently
   * substituting one for the other is what made this so hard to see.
   */
  // NB: every identity-token read below goes through getFallbackToken, which owns
  // freshness end to end; nothing reaches past it for a stored raw token.
  const renewImpersonation = useCallback(async (details: {
    targetUserId: string;
    organizationId: string;
    reason: string;
    sessionId?: string;
  }): Promise<string | undefined> => {
    if (renewalInFlightRef.current) return renewalInFlightRef.current.catch(() => undefined);

    const attempt = (async () => {
      const b2cToken = await getFallbackTokenRef.current();
      if (!b2cToken) throw new Error('No authentication token available — please sign in');

      // Refresh the existing session when we have one; only start a NEW session
      // if it is unrecoverable. Re-minting on every renewal is what let the token
      // and its session drift apart.
      const data =
        (details.sessionId ? await refreshImpersonationSession(b2cToken, details.sessionId) : undefined) ??
        (await mintImpersonationToken(
          b2cToken,
          details.targetUserId,
          details.organizationId,
          details.reason,
        ));
      const expiresAt = Date.now() + data.expires_in * 1000;
      const renewed = {
        targetUserId: details.targetUserId,
        organizationId: details.organizationId,
        sessionId: data.session_id,
        reason: details.reason,
        expiresAt,
      };

      applyImpersonationToken(data.access_token);
      applyImpersonationDetails(renewed);
      sessionStorage.setItem('impersonation_token', data.access_token);
      sessionStorage.setItem('impersonation_details', JSON.stringify(renewed));
      return data.access_token;
    })();

    renewalInFlightRef.current = attempt;
    try {
      return await attempt;
    } catch (e) {
      // Reported as `undefined` rather than rethrown. Deciding what a failed renewal MEANS is
      // the caller's job, and each caller ends its branch with one explicit throw. Rethrowing
      // here would instead surface inside the JSON-parse `catch` that wraps those branches, be
      // mistaken for corrupt details, and clean up + fall back to B2C — the exact silent
      // downgrade this change exists to remove.
      error('renewImpersonation: renewal failed', e);
      return undefined;
    } finally {
      renewalInFlightRef.current = null;
    }
  }, [applyImpersonationToken, applyImpersonationDetails]);

  const getEffectiveToken = useCallback(async () => {
    const detailsRaw = sessionStorage.getItem('impersonation_details');
    const tokenInMemory = impersonationTokenRef.current;

    // Prefer in-memory token (fast path)
    if (tokenInMemory && detailsRaw) {
      try {
        const details = JSON.parse(detailsRaw);
        const expiresAt = typeof details.expiresAt === 'number' ? details.expiresAt : 0;
        const fresh = expiresAt - Date.now() > IMPERSONATION_REFRESH_WINDOW_MS;
        log('getEffectiveToken: found in-memory impersonation token', { fresh, expiresAt });
        if (fresh) return tokenInMemory;

        // Expired, or close enough that an in-flight request could cross it. Renew rather
        // than fall through: the B2C token has no `organization_id`, so falling through
        // silently swaps the impersonated session for the real user's — which reads as a
        // server bug ("organizationId is required") anywhere org scope is needed, and as
        // wrong-but-successful data anywhere it is not.
        if (details.targetUserId && details.organizationId) {
          const renewed = await renewImpersonation({
            targetUserId: details.targetUserId,
            organizationId: details.organizationId,
            reason: details.reason ?? 'session renewal',
            // Lets renewal REFRESH this session rather than start a new one.
            sessionId: details.sessionId,
          });
          if (renewed) return renewed;
        }

        // Renewal impossible or failed. Surfacing beats downgrading — the caller sees a failed
        // request instead of silently acting as somebody else.
        error('getEffectiveToken: impersonation expired and could not be renewed');
        throw new Error('Impersonation session expired — please re-impersonate');
      } catch (e) {
        if (e instanceof Error && e.message.startsWith('Impersonation session expired')) throw e;
        warn('getEffectiveToken: parse error on details; falling back', e);
      }
    }

    // Try sessionStorage token if memory doesn’t have it
    if (!tokenInMemory) {
      const storedToken = sessionStorage.getItem('impersonation_token');
      if (storedToken && detailsRaw) {
        try {
          const details = JSON.parse(detailsRaw);
          const expiresAt = typeof details.expiresAt === 'number' ? details.expiresAt : 0;
          const fresh = expiresAt - Date.now() > IMPERSONATION_REFRESH_WINDOW_MS;
          log('getEffectiveToken: found sessionStorage impersonation token', { fresh, expiresAt });
          if (fresh) {
            // hydrate in-memory for future calls
            applyImpersonationToken(storedToken);
            applyImpersonationDetails(details);
            return storedToken;
          }

          // Stale or expired. This is the branch a page reload takes — the token is in
          // sessionStorage, not memory — and it used to CLEAR the session and fall through to
          // B2C, which is how an impersonating user silently became themselves again.
          if (details.targetUserId && details.organizationId) {
            const renewed = await renewImpersonation({
              targetUserId: details.targetUserId,
              organizationId: details.organizationId,
              reason: details.reason ?? 'session renewal',
              // Lets renewal REFRESH this session rather than start a new one.
              sessionId: details.sessionId,
            });
            if (renewed) return renewed;
          }

          // Only now is the session genuinely unusable. Clear it so the UI stops claiming to
          // impersonate, and surface rather than downgrade.
          sessionStorage.removeItem('impersonation_token');
          sessionStorage.removeItem('impersonation_details');
          applyImpersonationToken(null);
          applyImpersonationDetails(null);
          error('getEffectiveToken: impersonation expired and could not be renewed');
          throw new Error('Impersonation session expired — please re-impersonate');
        } catch (e) {
          if (e instanceof Error && e.message.startsWith('Impersonation session expired')) throw e;
          warn('getEffectiveToken: parse error on stored details; cleaning up', e);
          sessionStorage.removeItem('impersonation_token');
          sessionStorage.removeItem('impersonation_details');
        }
      }
    }

    // Fall back to the host app's identity token
    log('getEffectiveToken: using regular fallback token');
    return await getFallbackTokenRef.current();
    // Deliberately depends on nothing that changes: getFallbackToken is read from a ref
    // (see above) and the impersonation token from `impersonationTokenRef`, so this
    // function's identity is stable for the life of the provider.
  }, [applyImpersonationToken, renewImpersonation, applyImpersonationDetails]);

  // On mount, check for existing impersonation session
  React.useEffect(() => {
    const token = sessionStorage.getItem('impersonation_token');
    const details = sessionStorage.getItem('impersonation_details');

    if (!token || !details) {
      log('mount: no impersonation state in sessionStorage');
      return;
    }

    try {
      const parsed = JSON.parse(details);
      if (typeof parsed.expiresAt !== 'number') {
        warn('mount: details missing expiresAt; clearing');
        sessionStorage.removeItem('impersonation_token');
        sessionStorage.removeItem('impersonation_details');
        return;
      }

      if (parsed.expiresAt > Date.now()) {
        applyImpersonationToken(token);
        applyImpersonationDetails(parsed);
        log('mount: restored impersonation state', { sessionId: parsed.sessionId, expiresAt: parsed.expiresAt });
      } else {
        log('mount: found expired impersonation token; clearing');
        sessionStorage.removeItem('impersonation_token');
        sessionStorage.removeItem('impersonation_details');
      }
    } catch (e) {
      warn('mount: failed to parse impersonation_details; clearing', e);
      sessionStorage.removeItem('impersonation_token');
      sessionStorage.removeItem('impersonation_details');
    }
  }, [applyImpersonationToken, applyImpersonationDetails]);

  // The session's own scope, exposed only when both halves are known: a hook that
  // keys a cache by `${organizationId}::${systemName}` must never see a half-formed id.
  const sessionOrganizationId = sessionScope?.organizationId ?? null;
  const sessionUserId = sessionScope?.userId ?? null;
  const sessionDetails = React.useMemo<ActingDetails | null>(
    () =>
      sessionOrganizationId && sessionUserId
        ? { targetUserId: sessionUserId, organizationId: sessionOrganizationId, sessionId: null, reason: null }
        : null,
    [sessionOrganizationId, sessionUserId],
  );
  // An impersonation session, when there is one, overrides the session's own scope.
  const actingDetails: ActingDetails | null = impersonationDetails ?? sessionDetails;

  return (
    <ImpersonationContext.Provider
      value={React.useMemo(() => ({
      isImpersonating: !!impersonationToken,
      impersonationToken,
      impersonationDetails: actingDetails,
      startImpersonation,
      endImpersonation,
      getEffectiveToken
      }), [
      impersonationToken,
      actingDetails,
      startImpersonation,
      endImpersonation,
      getEffectiveToken
      ])}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
};
